import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import { Clock, Check, X, Car, Hash } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { ROUTES, buildRoute } from "@/constants/routes";
import {
  usePendingVehicles,
  useApproveVehicle,
  useRejectVehicle,
} from "../hooks/useVehicles";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from "@/components/ui/modal";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageSpinner } from "@/components/ui/spinner";
import { EmptyState } from "@/components/ui/empty-state";
import type { VehicleAPIResponse } from "@/types";
import { vehicleCapacityLabel } from "@/constants/vehicles";

function VerificationBadge({ status }: { status: string }) {
  const variant =
    status === "approved"
      ? "success"
      : status === "rejected"
        ? "destructive"
        : "warning";
  return (
    <Badge variant={variant}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

export function VehiclePendingPage() {
  const navigate = useNavigate();
  const { data: pendingVehicles, isLoading } = usePendingVehicles();

  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedVehicle, setSelectedVehicle] =
    useState<VehicleAPIResponse | null>(null);

  const { mutate: approve, isPending: approving } = useApproveVehicle();
  const { mutate: reject, isPending: rejecting } = useRejectVehicle();

  const handleApprove = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    approve(id);
  };

  const openRejectModal = (
    e: React.MouseEvent,
    vehicle: VehicleAPIResponse,
  ) => {
    e.stopPropagation();
    setSelectedVehicle(vehicle);
    setRejectModal(true);
  };

  const handleReject = () => {
    if (selectedVehicle && rejectReason.trim()) {
      reject(
        { id: selectedVehicle.id, reason: rejectReason },
        {
          onSuccess: () => {
            setRejectModal(false);
            setRejectReason("");
            setSelectedVehicle(null);
          },
        },
      );
    }
  };

  const closeModal = () => {
    setRejectModal(false);
    setRejectReason("");
    setSelectedVehicle(null);
  };

  if (isLoading) {
    return <PageSpinner message="Loading pending vehicle verifications..." />;
  }

  if (!pendingVehicles || pendingVehicles.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Pending Vehicle Verifications"
          breadcrumbs={[
            { label: "Vehicles", href: ROUTES.VEHICLES },
            { label: "Pending" },
          ]}
        />
        <EmptyState
          icon="default"
          title="No pending vehicle requests"
          description="All vehicle verifications are up to date."
          action={{
            label: "Go to Vehicles",
            onClick: () => navigate(ROUTES.VEHICLES),
          }}
        />
      </div>
    );
  }

  const columns: ColumnDef<VehicleAPIResponse>[] = [
    {
      accessorKey: "driver.user.name",
      header: "Driver",
      cell: ({ row }) => {
        const driverName = row.original.driver?.user?.name || "Unknown Driver";
        const driverPhone =
          row.original.driver?.user?.phone ||
          row.original.driver?.user?.mobile_number;

        return (
          <div className="flex items-center gap-3">
            <Avatar alt={driverName} size="sm" />
            <div>
              <p className="font-semibold">{driverName}</p>
              {driverPhone && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  {driverPhone}
                </p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "vehicle_model",
      header: "Vehicle Model",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 text-sm">
          <Car className="h-3.5 w-3.5 text-muted-foreground" />
          {row.original.vehicle_model || "—"}
        </div>
      ),
    },
    {
      accessorKey: "registration_number",
      header: "Registration Number",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 text-sm max-w-[200px]">
          <Hash className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <span className="truncate uppercase font-medium">
            {row.original.registration_number || "—"}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "capacity",
      header: "Capacity",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 text-sm">
          {vehicleCapacityLabel(row.original.vehicle_type)}
        </div>
      ),
    },
    {
      accessorKey: "verification_status",
      header: "Status",
      cell: ({ row }) => (
        <VerificationBadge status={row.original.verification_status} />
      ),
    },
    {
      accessorKey: "created_at",
      header: "Submitted",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          {row.original.created_at ? formatDate(row.original.created_at) : "—"}
        </div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {row.original.verification_status !== "approved" && (
            <Button
              size="sm"
              variant="success"
              className="px-2 h-8"
              onClick={(e) => handleApprove(e, row.original.id)}
              disabled={approving || rejecting}
            >
              <Check className="h-4 w-4 mr-1" /> Approve
            </Button>
          )}
          {row.original.verification_status !== "rejected" && (
            <Button
              size="sm"
              variant="destructive"
              className="px-2 h-8"
              onClick={(e) => openRejectModal(e, row.original)}
              disabled={approving || rejecting}
            >
              <X className="h-4 w-4 mr-1" /> Reject
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pending Vehicle Verifications"
        description={`${pendingVehicles.length} vehicle${pendingVehicles.length > 1 ? "s" : ""} pending verification`}
        breadcrumbs={[
          { label: "Vehicles", href: ROUTES.VEHICLES },
          { label: "Pending" },
        ]}
      />

      <DataTable
        columns={columns}
        data={pendingVehicles}
        loading={isLoading}
        showPagination={false}
        showSearch={false}
        onRowClick={(row) =>
          navigate(buildRoute(ROUTES.VEHICLE_DETAIL, { id: row.id }))
        }
      />

      {/* Reject Modal */}
      <Modal isOpen={rejectModal} onClose={closeModal}>
        <ModalHeader onClose={closeModal}>
          <ModalTitle>Reject Vehicle</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Please provide a reason for rejecting the{" "}
              <strong>{selectedVehicle?.vehicle_model}</strong> (
              {selectedVehicle?.registration_number}) verification request. The
              driver will be notified.
            </p>
            <div className="space-y-2">
              <Label required>Rejection Reason</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={4}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={closeModal} disabled={rejecting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleReject}
            isLoading={rejecting}
            disabled={!rejectReason.trim()}
          >
            Reject Vehicle
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
